/**
 * NC-DRAW-02 — the new-construction house's Money tab: draws to the builder
 * (each a real invoice), the builder's PO, the house's draw split (or the
 * builder's default), builder settings, and closing the house.
 * Data: ./ncBilling.ts. Seth 2026-09-24 approved the recommendation: "Each
 * builder gets a default draw split (e.g. 50/50), with the builder's PO
 * printed on every draw. Any house can be changed by hand."
 */
import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle, ArrowDown, ArrowUp, Check, CheckCircle2, ExternalLink, FileText, HandCoins, Loader2, Lock, Plus, Receipt, Settings2, Trash2, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { CollectPaymentModal } from "@/features/payments/CollectPaymentModal";
import { useCustomerPayments, invoicePaidMap } from "@/features/payments/payments";
import { useDeleteInvoice } from "@/features/invoice/officeInvoice";
import { usePostInstall } from "@/features/dispatch-detail/postInstall";
import {
  type Job, type PermitBy,
  useChangeOrders, useJobEquipment, useJobPermits, effectivePermitBy, permitIsOnFile, PERMIT_BY_LABEL,
} from "./jobs";
import {
  type Builder, type DrawStage, type DrawInvoice,
  useBuilder, useSaveBuilder, useNcDrawDefault, useJobDraws, useBillNcDraw, useSetJobBilling, useCloseNcJob,
  effectiveDrawPlan, OverContractError, DRAW_STAGE_NAMES, NC_DRAW_SPLIT_FALLBACK,
} from "./ncBilling";

const money = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (s: string | null | undefined) =>
  !s ? "" : new Date(s.length <= 10 ? s + "T12:00:00" : s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const fieldCls = "w-full rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Planned dollar amounts for a split; the last stage absorbs rounding so the
 *  plan always adds up to the contract. */
export function planAmounts(plan: DrawStage[], contract: number): number[] {
  const out: number[] = [];
  let sum = 0;
  plan.forEach((st, i) => {
    const last = i === plan.length - 1;
    const totalPct = plan.reduce((t, s) => t + s.pct, 0);
    const amt = last && Math.abs(totalPct - 100) < 0.01 ? round2(contract - sum) : round2((contract * st.pct) / 100);
    out.push(amt);
    sum += amt;
  });
  return out;
}

export function NCBillingSection({ job, closed }: { job: Job; closed: boolean }) {
  const { data: builder } = useBuilder(job.custno);
  const { data: orgDefault } = useNcDrawDefault();
  const { data: draws } = useJobDraws(job.id);
  const { data: cos } = useChangeOrders(job.id);
  const { data: payments } = useCustomerPayments(job.custno);
  const paidMap = useMemo(() => invoicePaidMap(payments), [payments]);
  const bill = useBillNcDraw();
  const setBilling = useSetJobBilling();
  const del = useDeleteInvoice();
  const canDelete = useCan("invoices.delete");
  const canCollect = useCan("payments.collect");

  const contract = (job.sell_price ?? 0) + (cos ?? []).filter((c) => c.status === "approved").reduce((s, c) => s + Number(c.amount || 0), 0);
  const all = (draws ?? []).filter((d) => d.status !== "void");
  const billed = all.reduce((s, d) => s + Number(d.total || 0), 0);
  const paidOf = (d: DrawInvoice) => Math.min(Number(d.total || 0), paidMap.get(d.id) ?? 0);
  const paid = all.reduce((s, d) => s + paidOf(d), 0);
  const { plan, source } = effectiveDrawPlan(job, builder, orgDefault);
  const amounts = planAmounts(plan, contract);

  // Match billed draws to plan stages by name, in order; the rest are extras.
  const used = new Set<string>();
  const stageRows = plan.map((st, i) => {
    const hit = all.find((d) => !used.has(d.id) && (d.label ?? "").toLowerCase() === st.label.toLowerCase());
    if (hit) used.add(hit.id);
    return { st, amount: amounts[i], inv: hit ?? null };
  });
  const extras = all.filter((d) => !used.has(d.id));
  const nextStage = stageRows.find((r) => !r.inv) ?? null;

  const [po, setPo] = useState(job.po_number ?? "");
  useEffect(() => { setPo(job.po_number ?? ""); }, [job.po_number]);
  const poDirty = po.trim() !== (job.po_number ?? "").trim();
  const [over, setOver] = useState<{ label: string; amount: number; pct: number | null; by: number } | null>(null);
  const [adhocStage, setAdhocStage] = useState("Progress");
  const [adhocAmt, setAdhocAmt] = useState("");
  const [editPlan, setEditPlan] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [collectFor, setCollectFor] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const doBill = (label: string, amount: number, pct: number | null, allowOver = false) => {
    setErr(null);
    bill.mutate({ jobId: job.id, label, amount, pct, allowOver }, {
      onSuccess: () => { setOver(null); setAdhocAmt(""); },
      onError: (e) => {
        if (e instanceof OverContractError) setOver({ label, amount, pct, by: e.over });
        else setErr(e instanceof Error ? e.message : "Couldn't bill the draw.");
      },
    });
  };
  const poMissing = !!builder?.po_required && !(job.po_number ?? "").trim();
  const openDraws = all.filter((d) => Number(d.total || 0) - paidOf(d) > 0.005);

  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-3 flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800">
        <Receipt className="h-[18px] w-[18px] text-brand-500" /> Draws to {job.customer_name ?? "the builder"}
        <InfoTip label="About draws">Each draw is a real invoice to the builder, with its own number, this house&apos;s PO, and a place on their balance and pay link.</InfoTip>
        <button type="button" onClick={() => setBuilderOpen(true)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-hairline px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">
          <Settings2 className="h-3.5 w-3.5" /> Builder Settings
        </button>
      </h3>

      {/* The builder's PO for this house */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" htmlFor="nc-po">Builder&apos;s PO</label>
        <InfoTip label="Where the PO prints">It prints on every draw from here on.</InfoTip>
        <input id="nc-po" value={po} disabled={closed} onChange={(e) => setPo(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && poDirty) setBilling.mutate({ id: job.id, po_number: po }); }}
          placeholder="PO # for this house" className={cn(fieldCls, "w-44 py-1.5")} />
        {poDirty && !closed && (
          <Button size="sm" variant="secondary" disabled={setBilling.isPending} onClick={() => setBilling.mutate({ id: job.id, po_number: po })}>
            <Check className="h-3.5 w-3.5" /> Save PO
          </Button>
        )}
        {poMissing && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700"><AlertCircle className="h-3 w-3" /> {job.customer_name ?? "This builder"} requires a PO</span>}
      </div>

      {/* The plan */}
      <div className="rounded-lg border border-hairline">
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline bg-slate-50/70 px-3 py-2 text-xs">
          <span className="font-semibold text-slate-600">Draw split</span>
          <span className="text-slate-500">{plan.map((s) => `${s.label} ${s.pct}%`).join(" · ")}</span>
          <span className="text-slate-400">— {source === "house" ? "set for this house" : source === "builder" ? `${job.customer_name ?? "the builder"}'s default` : "the company default"}</span>
          {!closed && (
            <button type="button" onClick={() => setEditPlan((v) => !v)} className="ml-auto text-xs font-semibold text-brand-700 hover:underline">
              {editPlan ? "Close" : "Change for this house"}
            </button>
          )}
        </div>
        {editPlan && (
          <div className="border-b border-hairline p-3">
            <DrawSplitEditor
              value={plan}
              onSave={(v) => setBilling.mutate({ id: job.id, draw_plan: v }, { onSuccess: () => setEditPlan(false) })}
              onReset={source === "house" ? () => setBilling.mutate({ id: job.id, draw_plan: null }, { onSuccess: () => setEditPlan(false) }) : undefined}
              resetLabel={builder?.draw_split?.length ? "Use the builder's default" : "Use the company default"}
              saving={setBilling.isPending}
            />
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {stageRows.map(({ st, amount, inv }) => (
            <li key={st.label} className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
              <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full", inv ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400")}>
                {inv ? <Check className="h-3.5 w-3.5" /> : <Receipt className="h-3.5 w-3.5" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="font-semibold text-slate-800">{st.label}</span>
                <span className="ml-1.5 text-xs text-slate-400">{st.pct}% · {money(amount)}</span>
              </span>
              {inv ? (
                <DrawRowActions inv={inv} paid={paidOf(inv)} canCollect={canCollect} canDelete={canDelete && !closed}
                  onCollect={() => setCollectFor(inv.id)} onDelete={() => setConfirmDel(inv.id)} />
              ) : !closed ? (
                <Button size="sm" variant={nextStage?.st.label === st.label ? "primary" : "secondary"} disabled={bill.isPending || amount <= 0}
                  onClick={() => doBill(st.label, amount, st.pct)}>
                  {bill.isPending && bill.variables?.label === st.label ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  Bill {st.label} — {money(amount)}
                </Button>
              ) : <span className="text-xs text-slate-400">not billed</span>}
            </li>
          ))}
          {extras.map((inv) => (
            <li key={inv.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-3.5 w-3.5" /></span>
              <span className="min-w-0 flex-1"><span className="font-semibold capitalize text-slate-800">{inv.label ?? "Draw"}</span><span className="ml-1.5 text-xs text-slate-400">extra draw</span></span>
              <DrawRowActions inv={inv} paid={paidOf(inv)} canCollect={canCollect} canDelete={canDelete && !closed}
                onCollect={() => setCollectFor(inv.id)} onDelete={() => setConfirmDel(inv.id)} />
            </li>
          ))}
        </ul>
      </div>

      {over && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertCircle className="h-4 w-4 shrink-0" />
          The {over.label} draw goes {money(over.by)} over the contract ({money(contract)}).
          <span className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setOver(null)}>Cancel</Button>
            <Button size="sm" onClick={() => doBill(over.label, over.amount, over.pct, true)}>Bill It Anyway</Button>
          </span>
        </div>
      )}
      {err && <p className="mt-2 flex items-center gap-1.5 text-[13px] text-rose-600"><AlertCircle className="h-3.5 w-3.5" /> {err}</p>}

      {!closed && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Extra draw</span>
          <select value={adhocStage} onChange={(e) => setAdhocStage(e.target.value)} className={cn(fieldCls, "w-32 py-1.5")} aria-label="Draw stage">
            {DRAW_STAGE_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <div className="relative w-28">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            <input type="number" value={adhocAmt} onChange={(e) => setAdhocAmt(e.target.value)} placeholder="amount" className={cn(fieldCls, "py-1.5 pl-6 text-right tabular-nums")} />
          </div>
          <Button size="sm" variant="secondary" disabled={!(Number(adhocAmt) > 0) || bill.isPending} onClick={() => doBill(adhocStage, round2(Number(adhocAmt)), null)}>
            <Plus className="h-3.5 w-3.5" /> Bill Draw
          </Button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-x-5 gap-y-1 border-t border-hairline pt-2.5 text-xs">
        <span className="text-slate-400">Contract <strong className="text-slate-700">{money(contract)}</strong></span>
        <span className="text-slate-400">Billed <strong className="text-slate-700">{money(billed)}</strong></span>
        <span className="text-slate-400">Paid <strong className="text-emerald-700">{money(paid)}</strong></span>
        <span className="text-slate-400">Open <strong className={cn(billed - paid > 0.005 ? "text-amber-700" : "text-slate-700")}>{money(billed - paid)}</strong></span>
        <span className="text-slate-400">Left to bill <strong className={cn("tabular-nums", Math.abs(contract - billed) < 0.01 ? "text-emerald-700" : "text-slate-700")}>{money(contract - billed)}</strong></span>
      </div>

      {builderOpen && <BuilderSettingsModal custno={job.custno} name={job.customer_name ?? job.custno} onClose={() => setBuilderOpen(false)} />}
      {collectFor && (
        <CollectPaymentModal
          custno={job.custno}
          customerName={job.customer_name ?? null}
          invoices={openDraws.map((d) => ({
            id: d.id,
            number: d.invoice_no || `SH-${d.id.slice(0, 6).toUpperCase()}`,
            date: d.completed_date,
            label: `${d.label ?? "Draw"} draw${job.title ? ` — ${job.title}` : ""}`,
            total: Number(d.total || 0),
            balance: round2(Number(d.total || 0) - paidOf(d)),
          }))}
          initialInvoiceId={collectFor}
          onClose={() => setCollectFor(null)}
        />
      )}
      {confirmDel && (
        <Modal open onClose={() => setConfirmDel(null)} title="Delete this draw?" size="sm">
          <p className="text-sm text-slate-600">The draw invoice and its line are removed for good. Only do this for a draw billed by mistake — a draw the builder already has should be credited instead.</p>
          {del.isError && <p className="mt-2 text-xs text-rose-600">{del.error instanceof Error ? del.error.message : "Delete failed."}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setConfirmDel(null)}>Keep It</Button>
            <Button variant="danger" size="sm" disabled={del.isPending} onClick={() => del.mutate(confirmDel, { onSuccess: () => setConfirmDel(null) })}>
              <Trash2 className="h-3.5 w-3.5" /> Delete Draw
            </Button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function DrawRowActions({ inv, paid, canCollect, canDelete, onCollect, onDelete }: {
  inv: DrawInvoice; paid: number; canCollect: boolean; canDelete: boolean; onCollect: () => void; onDelete: () => void;
}) {
  const total = Number(inv.total || 0);
  const open = round2(total - paid);
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs text-slate-500">#{inv.invoice_no || inv.id.slice(0, 6).toUpperCase()}</span>
      <span className="text-xs text-slate-400">{fmtDate(inv.completed_date ?? inv.created_at)}</span>
      <span className="font-semibold tabular-nums text-slate-800">{money(total)}</span>
      {open <= 0.005
        ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Paid</span>
        : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">{paid > 0.005 ? `${money(open)} open` : "Open"}</span>}
      <a href={`/invoices/${inv.id}`} target="_blank" rel="noreferrer" title="Open the invoice — print, email or send the pay link"
        className="inline-flex items-center gap-1 rounded-md border border-hairline px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
        <ExternalLink className="h-3 w-3" /> View
      </a>
      {canCollect && open > 0.005 && (
        <button type="button" onClick={onCollect} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-800">
          <HandCoins className="h-3 w-3" /> Collect
        </button>
      )}
      {canDelete && paid <= 0.005 && (
        <button type="button" onClick={onDelete} title="Delete this draw (billed by mistake)" className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

/** Stages + percents, in order; must total 100%. */
export function DrawSplitEditor({ value, onSave, onReset, resetLabel, saving, saveLabel = "Save Split" }: {
  value: DrawStage[];
  onSave: (v: DrawStage[]) => void;
  onReset?: () => void;
  resetLabel?: string;
  saving?: boolean;
  saveLabel?: string;
}) {
  const [rows, setRows] = useState<{ label: string; pct: string }[]>(() => value.map((s) => ({ label: s.label, pct: String(s.pct) })));
  const total = rows.reduce((t, r) => t + (Number(r.pct) || 0), 0);
  const valid = rows.length > 0 && rows.every((r) => r.label.trim() && Number(r.pct) > 0) && Math.abs(total - 100) < 0.01;
  const move = (i: number, d: -1 | 1) => setRows((rs) => {
    const j = i + d;
    if (j < 0 || j >= rs.length) return rs;
    const out = rs.slice(); [out[i], out[j]] = [out[j], out[i]]; return out;
  });
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex flex-col">
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
            <button type="button" disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
          </div>
          <input value={r.label} list="nc-stage-names" onChange={(e) => setRows((rs) => rs.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} placeholder="Stage" className={cn(fieldCls, "w-40 py-1.5")} aria-label="Stage name" />
          <div className="relative w-24">
            <input type="number" value={r.pct} onChange={(e) => setRows((rs) => rs.map((x, k) => (k === i ? { ...x, pct: e.target.value } : x)))} className={cn(fieldCls, "py-1.5 pr-6 text-right tabular-nums")} aria-label="Percent" />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
          </div>
          {rows.length > 1 && (
            <button type="button" onClick={() => setRows((rs) => rs.filter((_, k) => k !== i))} className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500" aria-label="Remove stage"><X className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ))}
      <datalist id="nc-stage-names">{DRAW_STAGE_NAMES.map((n) => <option key={n} value={n} />)}</datalist>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setRows((rs) => [...rs, { label: "", pct: String(Math.max(0, round2(100 - total))) }])} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline">
          <Plus className="h-3 w-3" /> Add a stage
        </button>
        <span className={cn("text-xs font-semibold tabular-nums", Math.abs(total - 100) < 0.01 ? "text-emerald-700" : "text-amber-700")}>Total {round2(total)}%{Math.abs(total - 100) < 0.01 ? "" : " — needs to be 100%"}</span>
        <span className="ml-auto flex gap-2">
          {onReset && <Button size="sm" variant="ghost" onClick={onReset}>{resetLabel ?? "Reset"}</Button>}
          <Button size="sm" disabled={!valid || saving} onClick={() => onSave(rows.map((r) => ({ label: r.label.trim(), pct: Number(r.pct) })))}>
            <Check className="h-3.5 w-3.5" /> {saving ? "Saving…" : saveLabel}
          </Button>
        </span>
      </div>
    </div>
  );
}

/** Per-builder defaults: draw split, who pulls permits, PO required, billing email. */
export function BuilderSettingsModal({ custno, name, onClose }: { custno: string; name: string; onClose: () => void }) {
  const { data: builder, isLoading } = useBuilder(custno);
  const { data: orgDefault } = useNcDrawDefault();
  const save = useSaveBuilder();
  const [permitBy, setPermitBy] = useState<PermitBy | null>(null);
  const [poRequired, setPoRequired] = useState(false);
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [split, setSplit] = useState<DrawStage[] | null>(null);
  const [editSplit, setEditSplit] = useState(false);
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || isLoading) return;
    setPermitBy(builder?.permit_by ?? null);
    setPoRequired(!!builder?.po_required);
    setEmail(builder?.billing_email ?? "");
    setNotes(builder?.notes ?? "");
    setSplit(builder?.draw_split ?? null);
    setSeeded(true);
  }, [builder, isLoading, seeded]);
  const shown = split ?? orgDefault ?? NC_DRAW_SPLIT_FALLBACK;
  const submit = () => save.mutate({ custno, draw_split: split, permit_by: permitBy, po_required: poRequired, billing_email: email, notes }, { onSuccess: onClose });

  return (
    <Modal open onClose={onClose} title="Builder Settings" subtitle={name} size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          {save.isError && <span className="mr-auto text-xs text-rose-600">Couldn&apos;t save — try again.</span>}
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={save.isPending || !seeded} onClick={submit}><Check className="h-3.5 w-3.5" /> {save.isPending ? "Saving…" : "Save"}</Button>
        </div>
      }>
      {!seeded ? <div className="h-40 animate-pulse rounded-lg bg-slate-100" /> : (
        <div className="space-y-5">
          <section>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Default draw split
              <InfoTip label="About the default split">It fills in on every one of their houses. Any house can still be changed by hand.</InfoTip>
            </div>
            {editSplit ? (
              <DrawSplitEditor value={shown} saveLabel="Use This Split"
                onSave={(v) => { setSplit(v); setEditSplit(false); }}
                onReset={split ? () => { setSplit(null); setEditSplit(false); } : undefined} resetLabel="Use the company default" />
            ) : (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-slate-800">{shown.map((s) => `${s.label} ${s.pct}%`).join(" · ")}</span>
                <span className="text-xs text-slate-400">{split ? "their own" : "the company default"}</span>
                <button type="button" onClick={() => setEditSplit(true)} className="text-xs font-semibold text-brand-700 hover:underline">Change</button>
              </div>
            )}
          </section>
          <section>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Who pulls the permit on their houses?
              <InfoTip label="How this default works">New houses for this builder start here. Each house&apos;s Permits tab can change it.</InfoTip>
            </div>
            <div className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-50 p-1 ring-1 ring-inset ring-slate-200">
              {([null, "sedgwick", "builder", "none"] as (PermitBy | null)[]).map((v) => (
                <button key={v ?? "ask"} type="button" onClick={() => setPermitBy(v)}
                  className={cn("rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors", permitBy === v ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-white")}>
                  {v ? PERMIT_BY_LABEL[v] : "Ask each house"}
                </button>
              ))}
            </div>
          </section>
          <section className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Send draws to</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ap@builder.com" className={fieldCls} />
              <span className="mt-1 block text-[11px] text-slate-400">Blank = the email on their customer record.</span>
            </label>
            <div className="flex items-center gap-1.5 pt-6">
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={poRequired} onChange={(e) => setPoRequired(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600" />
                <span>They require a PO on every draw</span>
              </label>
              <InfoTip label="What this setting does">A house without a PO shows a warning before billing.</InfoTip>
            </div>
          </section>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. Bills through Hyphen; super Rick J. approves extras." className={cn(fieldCls, "resize-none")} />
          </label>
        </div>
      )}
    </Modal>
  );
}

/** Closing a house: equipment onto the property record, locked. */
export function NCCloseOutSection({ job }: { job: Job }) {
  const { data: equip } = useJobEquipment(job.id);
  const { data: permits } = useJobPermits(job.id);
  const { data: draws } = useJobDraws(job.id);
  const { data: cos } = useChangeOrders(job.id);
  const { data: postInstall } = usePostInstall({ dispatch: job.source_dispatch ?? null, jobId: job.id });
  const close = useCloseNcJob();
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ posted: number; unbilled: number } | null>(null);
  const serials = (equip ?? []).filter((e) => (e.serial ?? "").trim()).length;
  const by = effectivePermitBy(job, postInstall ?? null);
  const permitOk = by === "builder" || by === "none" || (permits ?? []).some(permitIsOnFile);
  const contract = (job.sell_price ?? 0) + (cos ?? []).filter((c) => c.status === "approved").reduce((s, c) => s + Number(c.amount || 0), 0);
  const billed = (draws ?? []).reduce((s, d) => s + Number(d.total || 0), 0);
  const unbilled = round2(contract - billed);
  const ready = serials > 0 && permitOk;

  if (done || job.status === "closed") {
    return (
      <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 text-sm text-emerald-900">
        <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> House closed{job.closed_at ? ` ${fmtDate(job.closed_at)}` : ""}</div>
        <p className="mt-1">
          {done ? `${done.posted} unit${done.posted === 1 ? "" : "s"} moved` : "Its equipment is"} onto the property record — ready for the homeowner handoff below.
          {unbilled > 0.005 ? ` ${money(unbilled)} of the contract was never billed.` : ""}
        </p>
      </section>
    );
  }
  return (
    <section className="rounded-xl border border-brand-200 bg-brand-50/30 p-4">
      <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-800">
        <Lock className="h-[18px] w-[18px] text-brand-500" /> Close the House
        <InfoTip label="What closing does">Closing puts the house&apos;s equipment and serials on the property record, so the homeowner inherits the system and warranty, and locks the job. The draws are the invoices, so closing makes no new one.</InfoTip>
      </h3>
      <div className="space-y-1.5 text-sm">
        <Line ok={serials > 0} label={`Serial numbers logged (${serials})`} />
        <Line ok={permitOk} label={by === "builder" ? "Permit — the builder pulls it" : by === "none" ? "Permit — not needed" : permitOk ? "Permit on file" : "Permit — none of ours on file yet"} />
        <Line ok={Math.abs(unbilled) < 0.01} soft label={Math.abs(unbilled) < 0.01 ? "Contract fully billed" : unbilled > 0 ? `${money(unbilled)} of the contract not billed yet — doesn't block closing` : `Billed ${money(-unbilled)} over the contract`} />
      </div>
      {err && <p className="mt-2 flex items-start gap-1.5 text-[13px] text-rose-600"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {err}</p>}
      <Button className="mt-3" disabled={!ready || close.isPending}
        onClick={() => { setErr(null); close.mutate(job.id, { onSuccess: (r) => setDone({ posted: r.equipment_posted, unbilled: Number(r.unbilled) || 0 }), onError: (e) => setErr(e instanceof Error ? e.message : "Close failed.") }); }}>
        <Lock className="h-4 w-4" /> {close.isPending ? "Closing…" : "Close the House"}
      </Button>
    </section>
  );
}

function Line({ ok, label, soft }: { ok: boolean; label: string; soft?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertCircle className={cn("h-3.5 w-3.5", soft ? "text-slate-400" : "text-amber-500")} />}
      <span className={ok ? "text-slate-600" : "text-slate-500"}>{label}</span>
    </div>
  );
}

export type { Builder };
